import api from './api';
import axios from 'axios';
import { packUploadBatches } from '../lib/cfos/droppedFiles';
import type {
  Annotation,
  AnnotationDraft,
  CfosSandbox,
  FilePath,
  FileRecord,
  LiveEvent,
  LivePollResult,
  OfficeCheckinResult,
  OfficeSession,
  OfficeSessionResult,
  RevisionAssignInput,
  SearchFilters,
  StorageUsage,
  UploadBatchProgress,
  UploadItem,
  UploadResult,
  User,
} from '../lib/cfos/types';

const UPLOAD_RETRY_DELAY_MS = 1000;

export class CfosApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'CfosApiError';
    this.status = status;
    this.data = data;
  }
}

type FileDto = Omit<FileRecord, 'tags' | 'tag_ids' | 'updated_at'> & {
  tags?: string[] | Array<{ id: string; tag: string }>;
  tag_ids?: Record<string, string>;
  updated_at?: string;
};

type AnnotationDto = {
  id: string;
  file_id: string;
  author_id: number | string;
  user_type: string;
  color: string;
  payload: Record<string, unknown>;
  version: number;
  created_at: string;
  updated_at: string;
  author: User;
};

type UploadResultDto = {
  input_filename: string;
  status: string;
  file: FileDto | null;
  existing_paths: FilePath[];
  detail: string | null;
};

function extractErrorMessage(data: unknown, fallback: string): string {
  if (typeof data === 'object' && data) {
    const obj = data as { error?: string; message?: string; detail?: unknown };
    if (typeof obj.error === 'string') return obj.error;
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.detail === 'string') return obj.detail;
  }
  return fallback;
}

function toCfosApiError(error: unknown, fallback: string): CfosApiError {
  if (error instanceof CfosApiError) return error;
  if (axios.isAxiosError(error)) {
    return new CfosApiError(
      extractErrorMessage(error.response?.data, fallback),
      error.response?.status || 0,
      error.response?.data,
    );
  }
  if (error instanceof Error) {
    return new CfosApiError(error.message || fallback, 0);
  }
  return new CfosApiError(fallback, 0);
}

function mapFile(value: FileDto): FileRecord {
  let tags: string[] = [];
  let tagIds: Record<string, string> = {};

  if (Array.isArray(value.tags)) {
    if (value.tags.length && typeof value.tags[0] === 'object') {
      const tagRows = value.tags as Array<{ id: string; tag: string }>;
      tags = tagRows.map((item) => item.tag);
      tagIds = Object.fromEntries(tagRows.map((item) => [item.tag, item.id]));
    } else {
      tags = value.tags as string[];
      tagIds = value.tag_ids || {};
    }
  }

  return {
    ...value,
    tags,
    tag_ids: tagIds,
    engineering_document_id: value.engineering_document_id ?? null,
    engineering_document_name: value.engineering_document_name ?? null,
    revision_number: value.revision_number ?? null,
    same_file_group_id: value.same_file_group_id ?? null,
    is_contractual: Boolean(value.is_contractual),
    contractual_orphan: Boolean(value.contractual_orphan),
    updated_at: value.updated_at || value.created_at,
    paths: value.paths || [],
    office_session: value.office_session ?? null,
  };
}

function annotationPayload(value: Annotation | AnnotationDraft) {
  return {
    ...value.payload,
    kind: value.kind,
    page: value.page,
    ...(value.comment ? { comment: value.comment } : {}),
  };
}

function mapAnnotation(value: AnnotationDto & { annotation_color?: string }): Annotation {
  const { payload } = value;
  return {
    id: value.id,
    file_id: value.file_id,
    kind: (typeof payload.kind === 'string' ? payload.kind : 'note') as Annotation['kind'],
    page: typeof payload.page === 'number' ? payload.page : 1,
    payload,
    comment: typeof payload.comment === 'string' ? payload.comment : undefined,
    author: value.author,
    annotation_color: value.color || value.annotation_color || '#64748B',
    version: value.version,
    created_at: value.created_at,
    updated_at: value.updated_at,
  };
}

function uniqueLocationPaths(primary: FilePath[], fallback: FilePath[] = []): string[] {
  const seen = new Set<string>();
  const paths: string[] = [];
  for (const location of [...primary, ...fallback]) {
    if (!location.path || seen.has(location.path)) continue;
    seen.add(location.path);
    paths.push(location.path);
  }
  return paths;
}

function mapUploadResults(results: UploadResultDto[]): UploadResult {
  return {
    uploaded: results
      .filter((item) => item.status === 'uploaded' && item.file)
      .map((item) => mapFile(item.file!)),
    linked: results
      .filter((item) => item.status === 'linked' && item.file)
      .map((item) => ({
        filename: item.input_filename,
        file: mapFile(item.file!),
        other_locations: item.existing_paths.map((location) => location.path),
        reason: item.detail || undefined,
      })),
    duplicates: results
      .filter((item) => item.status === 'duplicate')
      .map((item) => ({
        filename: item.input_filename,
        exists_in: uniqueLocationPaths(item.existing_paths, item.file?.paths ?? []),
        reason: item.detail || undefined,
      })),
    errors: results
      .filter((item) => !['uploaded', 'linked', 'duplicate'].includes(item.status))
      .map((item) => ({
        filename: item.input_filename,
        message: item.detail || 'Upload was rejected',
      })),
  };
}

function mergeUploadResult(target: UploadResult, source: UploadResult) {
  target.uploaded.push(...source.uploaded);
  target.linked.push(...source.linked);
  target.duplicates.push(...source.duplicates);
  target.errors = [...(target.errors || []), ...(source.errors || [])];
}

function isRetryableUploadError(error: unknown): boolean {
  if (!(error instanceof CfosApiError)) return true;
  return error.status === 0 || error.status === 502 || error.status === 503 || error.status === 504;
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new CfosApiError('Upload cancelled', 0));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(new CfosApiError('Upload cancelled', 0));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

function recordBatchFailure(target: UploadResult, batch: UploadItem[], error: unknown) {
  const message = error instanceof Error ? error.message : 'Upload batch failed';
  for (const item of batch) {
    target.errors = [...(target.errors || []), { filename: item.file.name, message }];
  }
}

async function uploadRetrying(
  items: UploadItem[],
  sandboxId: string,
  onProgress?: (progress: number) => void,
  signal?: AbortSignal,
): Promise<UploadResult> {
  try {
    return await cfosService.upload(items, sandboxId, onProgress);
  } catch (error) {
    if (signal?.aborted) throw error;
    if (!isRetryableUploadError(error)) throw error;
    await wait(UPLOAD_RETRY_DELAY_MS, signal);
    return await cfosService.upload(items, sandboxId, onProgress);
  }
}

export function parseLiveEvent(value: unknown): LiveEvent | null {
  if (!value || typeof value !== 'object') return null;
  const event = value as Record<string, unknown>;
  const type = event.type;
  if (type === 'presence.snapshot' && Array.isArray(event.users)) {
    return { kind: 'snapshot', users: event.users as LiveEvent['users'] };
  }
  if ((type === 'presence.joined' || type === 'presence.left') && event.user) {
    return {
      kind: type,
      user: event.user as LiveEvent['user'],
      timestamp: typeof event.timestamp === 'string' ? event.timestamp : undefined,
    };
  }
  if (type === 'annotation.deleted' && typeof event.annotation_id === 'string') {
    return {
      kind: 'annotation.deleted',
      annotation_id: event.annotation_id,
      version: typeof event.version === 'number' ? event.version : 0,
      timestamp: typeof event.timestamp === 'string' ? event.timestamp : undefined,
      client_operation_id: typeof event.client_operation_id === 'string' ? event.client_operation_id : undefined,
    };
  }
  if (
    (type === 'annotation.created' || type === 'annotation.updated' || type === 'annotation.moved')
    && event.annotation
  ) {
    return {
      kind: 'annotation.upserted',
      annotation: mapAnnotation(event.annotation as AnnotationDto),
      timestamp: typeof event.timestamp === 'string' ? event.timestamp : undefined,
      client_operation_id: typeof event.client_operation_id === 'string' ? event.client_operation_id : undefined,
    };
  }
  return null;
}

const cfosService = {
  async me(): Promise<User> {
    const response = await api.get<User>('/cfos/me');
    return response.data;
  },

  async storage(): Promise<StorageUsage> {
    const response = await api.get<StorageUsage>('/cfos/files/storage');
    return response.data;
  },

  async listSandboxes(projectId?: number): Promise<CfosSandbox[]> {
    const response = await api.get<CfosSandbox[]>('/cfos/sandboxes', {
      params: projectId != null ? { project_id: projectId } : undefined,
    });
    return response.data;
  },

  async getOrCreateSandbox(projectId?: number | null): Promise<CfosSandbox> {
    const response = await api.post<CfosSandbox>('/cfos/sandboxes', {
      project_id: projectId ?? null,
    });
    return response.data;
  },

  async listFolders(sandboxId: string): Promise<{
    paths: string[];
    folders: { path: string; role_ids: number[] }[];
    can_create_folders: boolean;
    roles: { id: number; name: string }[];
  }> {
    try {
      const response = await api.get<{
        paths: string[];
        folders?: { path: string; role_ids: number[] }[];
        can_create_folders?: boolean;
        roles?: { id: number; name: string }[];
      }>(`/cfos/sandboxes/${sandboxId}/folders`);
      const folders = response.data.folders
        || (response.data.paths || []).map((path) => ({ path, role_ids: [] as number[] }));
      return {
        paths: folders.map((item) => item.path),
        folders,
        can_create_folders: response.data.can_create_folders !== false,
        roles: response.data.roles || [],
      };
    } catch (error) {
      throw toCfosApiError(error, 'Could not load folders');
    }
  },

  async createFolders(
    sandboxId: string,
    paths: string[],
    roleIds: number[] = [],
  ): Promise<string[]> {
    try {
      const response = await api.post<{ paths: string[] }>(`/cfos/sandboxes/${sandboxId}/folders`, {
        paths,
        role_ids: roleIds,
      });
      return response.data.paths || [];
    } catch (error) {
      throw toCfosApiError(error, 'Could not create folders');
    }
  },

  async renameFolder(sandboxId: string, path: string, name: string): Promise<{
    path: string;
    paths: string[];
    folders: { path: string; role_ids: number[] }[];
  }> {
    try {
      const response = await api.patch<{
        path: string;
        paths?: string[];
        folders?: { path: string; role_ids: number[] }[];
      }>(`/cfos/sandboxes/${sandboxId}/folders`, { path, name });
      const folders = response.data.folders
        || (response.data.paths || []).map((item) => ({ path: item, role_ids: [] as number[] }));
      return {
        path: response.data.path,
        paths: folders.map((item) => item.path),
        folders,
      };
    } catch (error) {
      throw toCfosApiError(error, 'Could not rename folder');
    }
  },

  async updateFolderVisibility(
    sandboxId: string,
    path: string,
    roleIds: number[],
  ): Promise<{
    paths: string[];
    folders: { path: string; role_ids: number[] }[];
  }> {
    try {
      const response = await api.put<{
        paths?: string[];
        folders?: { path: string; role_ids: number[] }[];
      }>(`/cfos/sandboxes/${sandboxId}/folders`, { path, role_ids: roleIds });
      const folders = response.data.folders
        || (response.data.paths || []).map((item) => ({ path: item, role_ids: [] as number[] }));
      return {
        paths: folders.map((item) => item.path),
        folders,
      };
    } catch (error) {
      throw toCfosApiError(error, 'Could not update folder visibility');
    }
  },

  async deleteFolder(sandboxId: string, path: string): Promise<void> {
    try {
      await api.delete(`/cfos/sandboxes/${sandboxId}/folders`, { data: { path } });
    } catch (error) {
      throw toCfosApiError(error, 'Could not remove folder');
    }
  },

  async getFolderTemplates(projectTypeId: number): Promise<{
    folders: { path: string; role_ids: number[] }[];
    paths: string[];
    roles: { id: number; name: string }[];
  }> {
    const response = await api.get<{
      folders?: { path: string; role_ids: number[] }[];
      paths?: string[];
      roles?: { id: number; name: string }[];
    }>('/cfos/folder-templates', {
      params: { project_type_id: projectTypeId },
    });
    const folders = response.data.folders
      || (response.data.paths || []).map((path) => ({ path, role_ids: [] as number[] }));
    return {
      folders,
      paths: folders.map((f) => f.path),
      roles: response.data.roles || [],
    };
  },

  async saveFolderTemplates(
    projectTypeId: number,
    folders: { path: string; role_ids: number[] }[],
  ): Promise<{ path: string; role_ids: number[] }[]> {
    const response = await api.put<{
      folders?: { path: string; role_ids: number[] }[];
      paths?: string[];
    }>('/cfos/folder-templates', {
      project_type_id: projectTypeId,
      folders,
    });
    return response.data.folders
      || (response.data.paths || []).map((path) => ({ path, role_ids: [] }));
  },

  async applyFolderTemplates(projectTypeId: number): Promise<{ folders_added: number }> {
    const response = await api.post<{ folders_added: number }>('/cfos/folder-templates/apply', {
      project_type_id: projectTypeId,
    });
    return response.data;
  },

  async getFolderCreateAcl(): Promise<{
    shared: { role_ids: number[] };
    project: { role_ids: number[] };
    roles: { id: number; name: string }[];
  }> {
    const response = await api.get<{
      shared: { role_ids: number[] };
      project: { role_ids: number[] };
      roles: { id: number; name: string }[];
    }>('/cfos/folder-create-acl');
    return response.data;
  },

  async saveFolderCreateAcl(payload: {
    shared: { role_ids: number[] };
    project: { role_ids: number[] };
  }): Promise<{
    shared: { role_ids: number[] };
    project: { role_ids: number[] };
    roles: { id: number; name: string }[];
  }> {
    const response = await api.put('/cfos/folder-create-acl', payload);
    return response.data;
  },

  async files(options?: { path?: string; sandboxId?: string | null }): Promise<FileRecord[]> {
    const pageSize = 500;
    const all: FileRecord[] = [];
    let offset = 0;
    for (;;) {
      const params: Record<string, string | number> = { limit: pageSize, offset };
      if (options?.path && options.path !== '/') {
        params.path = options.path;
      }
      if (options?.sandboxId) {
        params.sandbox_id = options.sandboxId;
      }
      const response = await api.get<FileDto[]>('/cfos/files', { params });
      const values = response.data;
      all.push(...values.map(mapFile));
      if (values.length < pageSize) break;
      offset += pageSize;
    }
    return all;
  },

  async file(id: string): Promise<FileRecord> {
    const response = await api.get<FileDto>(`/cfos/files/${id}`);
    return mapFile(response.data);
  },

  async search(filters: SearchFilters & { sandboxId?: string | null }): Promise<FileRecord[]> {
    const response = await api.get<FileDto[]>('/cfos/files', {
      params: {
        q: filters.q,
        tag: filters.tag,
        path: filters.path,
        uploader: filters.uploader,
        uploaded_from: filters.date_from,
        uploaded_to: filters.date_to,
        sandbox_id: filters.sandboxId || undefined,
      },
    });
    return response.data.map(mapFile);
  },

  async upload(
    items: UploadItem[],
    sandboxId: string,
    onProgress?: (progress: number) => void,
  ): Promise<UploadResult> {
    if (!items.length) {
      return { uploaded: [], linked: [], duplicates: [], errors: [] };
    }
    const form = new FormData();
    items.forEach((item) => {
      form.append('files', item.file);
      form.append('paths', item.path);
    });
    form.append('path', items[0].path);
    form.append('sandbox_id', sandboxId);
    const response = await api.post<UploadResultDto[]>('/cfos/files/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (event) => {
            if (event.total) onProgress(Math.round((event.loaded * 100) / event.total));
          }
        : undefined,
    });
    return mapUploadResults(response.data);
  },

  async uploadInBatches(
    items: UploadItem[],
    sandboxId: string,
    options: {
      onProgress?: (progress: UploadBatchProgress) => void;
      signal?: AbortSignal;
    } = {},
  ): Promise<UploadResult> {
    const batches = packUploadBatches(items);
    const merged: UploadResult = { uploaded: [], linked: [], duplicates: [], errors: [] };
    let completed = 0;
    options.onProgress?.({ completed: 0, total: items.length, batchIndex: 0, batchCount: batches.length });

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex += 1) {
      if (options.signal?.aborted) {
        throw new CfosApiError('Upload cancelled', 0);
      }
      const batch = batches[batchIndex];
      const reportProgress = () => {
        options.onProgress?.({
          completed,
          total: items.length,
          batchIndex: batchIndex + 1,
          batchCount: batches.length,
        });
      };
      try {
        const result = await uploadRetrying(batch, sandboxId, undefined, options.signal);
        mergeUploadResult(merged, result);
        completed += batch.length;
        reportProgress();
        continue;
      } catch (batchError) {
        if (options.signal?.aborted) throw batchError;
        if (batch.length === 1 || !isRetryableUploadError(batchError)) {
          recordBatchFailure(merged, batch, batchError);
          completed += batch.length;
          reportProgress();
          continue;
        }
      }

      await wait(UPLOAD_RETRY_DELAY_MS, options.signal);
      for (const item of batch) {
        if (options.signal?.aborted) {
          throw new CfosApiError('Upload cancelled', 0);
        }
        try {
          mergeUploadResult(
            merged,
            await uploadRetrying([item], sandboxId, undefined, options.signal),
          );
        } catch (fileError) {
          recordBatchFailure(merged, [item], fileError);
        }
        completed += 1;
        reportProgress();
      }
    }
    return merged;
  },

  async checkFilename(filename: string): Promise<{ exists: boolean; matches: FileRecord[] }> {
    const response = await api.get<{ exists: boolean; matches: FileDto[] }>('/cfos/files/preflight', {
      params: { filename },
    });
    return {
      exists: response.data.exists,
      matches: response.data.matches.map(mapFile),
    };
  },

  async addPath(fileId: string, path: string, sandboxId: string): Promise<FilePath> {
    const response = await api.post<FilePath>(`/cfos/files/${fileId}/paths`, {
      path,
      sandbox_id: sandboxId,
    });
    return response.data;
  },

  async movePath(fileId: string, pathId: string, path: string): Promise<FilePath> {
    const response = await api.put<FilePath>(`/cfos/files/${fileId}/paths/${pathId}`, { path });
    return response.data;
  },

  async deletePath(fileId: string, pathId: string): Promise<void> {
    await api.delete(`/cfos/files/${fileId}/paths/${pathId}`);
  },

  async listTags(q?: string, sandboxId?: string | null): Promise<string[]> {
    const response = await api.get<string[]>('/cfos/files/tags', {
      params: { q, limit: 500, sandbox_id: sandboxId || undefined },
    });
    return response.data;
  },

  async addTag(fileId: string, tag: string, sandboxId: string): Promise<{ id: string; tag: string }> {
    const response = await api.post<{ id: string; tag: string }>(`/cfos/files/${fileId}/tags`, {
      tag,
      sandbox_id: sandboxId,
    });
    return response.data;
  },

  async addTags(fileIds: string[], tag: string, sandboxId: string): Promise<Array<{ id: string; tag: string }>> {
    const response = await api.post<Array<{ id: string; tag: string }>>('/cfos/files/tags', {
      tag,
      file_ids: fileIds,
      sandbox_id: sandboxId,
    });
    return response.data;
  },

  async removeTag(fileId: string, tagId: string): Promise<void> {
    await api.delete(`/cfos/files/${fileId}/tags/${tagId}`);
  },

  async revisions(id: string): Promise<FileRecord[]> {
    const response = await api.get<FileDto[]>(`/cfos/files/${id}/revisions`);
    return response.data.map(mapFile);
  },

  async assignRevision(targetFileId: string, payload: RevisionAssignInput): Promise<FileRecord[]> {
    const response = await api.post<FileDto[]>(`/cfos/files/${targetFileId}/revisions`, payload);
    return response.data.map(mapFile);
  },

  async renumberRevision(fileId: string, revisionNumber: number): Promise<FileRecord> {
    const response = await api.patch<FileDto>(`/cfos/files/${fileId}/revision`, {
      revision_number: revisionNumber,
    });
    return mapFile(response.data);
  },

  async unassignRevision(fileId: string): Promise<void> {
    await api.delete(`/cfos/files/${fileId}/revision`);
  },

  async sameFiles(id: string): Promise<FileRecord[]> {
    const response = await api.get<FileDto[]>(`/cfos/files/${id}/same-files`);
    return response.data.map(mapFile);
  },

  async linkSameFiles(fileIds: string[]): Promise<FileRecord[]> {
    const response = await api.post<FileDto[]>('/cfos/files/same-files', { file_ids: fileIds });
    return response.data.map(mapFile);
  },

  async unlinkSameFile(fileId: string): Promise<void> {
    await api.delete(`/cfos/files/${fileId}/same-files`);
  },

  async setContractualRevision(fileId: string): Promise<FileRecord> {
    const response = await api.put<FileDto>(`/cfos/files/${fileId}/contractual`);
    return mapFile(response.data);
  },

  async clearContractualRevision(fileId: string): Promise<void> {
    await api.delete(`/cfos/files/${fileId}/contractual`);
  },

  fileContentUrl(id: string): string {
    const base = process.env.REACT_APP_API_URL || '/api';
    return `${base.replace(/\/$/, '')}/cfos/files/${id}/content`;
  },

  async fileContentBlob(id: string): Promise<Blob> {
    const token = localStorage.getItem('access_token');
    const response = await fetch(this.fileContentUrl(id), {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      throw new CfosApiError(`File could not be loaded (${response.status})`, response.status);
    }
    return response.blob();
  },

  async officeSession(fileId: string): Promise<OfficeSessionResult> {
    try {
      const response = await api.get<{ session: OfficeSession | null; file: FileDto }>(
        `/cfos/files/${fileId}/office/session`,
      );
      return {
        session: response.data.session,
        file: mapFile(response.data.file),
      };
    } catch (error) {
      throw toCfosApiError(error, 'Could not load office session');
    }
  },

  async checkoutOffice(fileId: string): Promise<OfficeSessionResult> {
    try {
      const response = await api.post<{ session: OfficeSession; file: FileDto }>(
        `/cfos/files/${fileId}/office/checkout`,
      );
      return {
        session: response.data.session,
        file: mapFile(response.data.file),
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const data = error.response.data as {
          session?: OfficeSession;
          file?: FileDto;
          join?: boolean;
          error?: string;
        };
        if (data?.session) {
          return {
            session: data.session,
            file: data.file ? mapFile(data.file) : await this.file(fileId),
            join: true,
            error: data.error,
          };
        }
      }
      throw toCfosApiError(error, 'Microsoft 365 checkout failed');
    }
  },

  async joinOffice(fileId: string): Promise<OfficeSessionResult> {
    try {
      const response = await api.post<{ session: OfficeSession; file: FileDto }>(
        `/cfos/files/${fileId}/office/join`,
      );
      return {
        session: response.data.session,
        file: mapFile(response.data.file),
      };
    } catch (error) {
      throw toCfosApiError(error, 'Could not join Microsoft 365 session');
    }
  },

  async checkinOffice(fileId: string): Promise<OfficeCheckinResult> {
    try {
      const response = await api.post<{
        action: OfficeCheckinResult['action'];
        file: FileDto;
        session: OfficeSession | null;
      }>(`/cfos/files/${fileId}/office/checkin`);
      return {
        action: response.data.action,
        file: mapFile(response.data.file),
        session: response.data.session,
      };
    } catch (error) {
      throw toCfosApiError(error, 'Check-in failed');
    }
  },

  async discardOffice(fileId: string): Promise<OfficeSessionResult> {
    try {
      const response = await api.post<{ file: FileDto; session: OfficeSession | null }>(
        `/cfos/files/${fileId}/office/discard`,
      );
      return {
        session: response.data.session,
        file: mapFile(response.data.file),
      };
    } catch (error) {
      throw toCfosApiError(error, 'Discard failed');
    }
  },

  async previewOffice(fileId: string): Promise<OfficeSessionResult> {
    try {
      const response = await api.get<{
        preview_url: string;
        session: OfficeSession | null;
        file: FileDto;
      }>(`/cfos/files/${fileId}/office/preview`);
      return {
        preview_url: response.data.preview_url,
        session: response.data.session,
        file: mapFile(response.data.file),
      };
    } catch (error) {
      throw toCfosApiError(error, 'Microsoft 365 preview failed');
    }
  },

  async annotations(fileId: string, sandboxId: string): Promise<Annotation[]> {
    const response = await api.get<AnnotationDto[]>(`/cfos/documents/${fileId}/annotations`, {
      params: { sandbox_id: sandboxId },
    });
    return response.data.map(mapAnnotation);
  },

  async createAnnotation(
    fileId: string,
    draft: AnnotationDraft,
    clientOperationId: string,
    sandboxId: string,
  ): Promise<Annotation> {
    const response = await api.post<AnnotationDto>(`/cfos/documents/${fileId}/annotations`, {
      payload: annotationPayload(draft),
      client_operation_id: clientOperationId,
      sandbox_id: sandboxId,
    });
    return mapAnnotation(response.data);
  },

  async updateAnnotation(
    annotation: Annotation,
    clientOperationId: string,
    sandboxId: string,
  ): Promise<Annotation> {
    const response = await api.put<AnnotationDto>(
      `/cfos/documents/${annotation.file_id}/annotations/${annotation.id}`,
      {
        payload: annotationPayload(annotation),
        expected_version: annotation.version,
        client_operation_id: clientOperationId,
        sandbox_id: sandboxId,
      },
    );
    return mapAnnotation(response.data);
  },

  async moveAnnotation(
    annotation: Annotation,
    clientOperationId: string,
    sandboxId: string,
  ): Promise<Annotation> {
    const response = await api.post<AnnotationDto>(
      `/cfos/documents/${annotation.file_id}/annotations/${annotation.id}/move`,
      {
        payload: annotationPayload(annotation),
        expected_version: annotation.version,
        client_operation_id: clientOperationId,
        sandbox_id: sandboxId,
      },
    );
    return mapAnnotation(response.data);
  },

  async deleteAnnotation(
    fileId: string,
    id: string,
    version: number,
    clientOperationId: string,
    sandboxId: string,
  ): Promise<void> {
    await api.delete(`/cfos/documents/${fileId}/annotations/${id}`, {
      params: {
        expected_version: version,
        client_operation_id: clientOperationId,
        sandbox_id: sandboxId,
      },
    });
  },

  async livePoll(fileId: string, sandboxId: string, since?: string): Promise<LivePollResult> {
    const response = await api.get<{ events?: unknown[]; annotations?: AnnotationDto[]; users: User[]; server_time?: string }>(
      `/cfos/documents/${fileId}/live`,
      { params: { sandbox_id: sandboxId, ...(since ? { since } : {}) } },
    );
    const data = response.data;
    return {
      ...data,
      users: data.users || [],
      annotations: data.annotations?.map(mapAnnotation),
    };
  },

  async presenceHeartbeat(fileId: string, sandboxId: string): Promise<User[]> {
    const response = await api.post<{ users: User[] }>(`/cfos/documents/${fileId}/presence`, {
      sandbox_id: sandboxId,
    });
    return response.data.users || [];
  },
};

export default cfosService;
